import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  View, Text, StyleSheet, Pressable,
} from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { useSelector } from 'react-redux';
import EllipsisIcon from '../../../components/EllipsisIcon';
import ShareTicketPopup from './ShareTicketPopup';
import Colors from '../../../constants/Colors';
import Style from '../../../constants/Style';
import { logEvent } from '../../../components/LogEvent';
import PressableStyled from '../../../components/PressableStyled';
import isMobile from '../../../components/IsMobile';
import SelectionModal from '../../../components/SelectionModal';
import AlertModal from '../../../components/AlertModal';
import { Ticket, UserPermission } from '../../../constants/Types';

interface TicketSharePaneProps {
  isDev: boolean,
  shareDropdownShown: boolean,
  setShareDropdownShown: Dispatch<SetStateAction<boolean>>,
  shareConfirmationShown: boolean,
  setShareConfirmationShown: Dispatch<SetStateAction<boolean>>,
  selectedTicket: Ticket,
  deleteTicket: () => void,
  clientName: string,
  selectedBuilding: string,
}

const TicketSharePane = ({
  isDev, shareDropdownShown, setShareDropdownShown, shareConfirmationShown, selectedTicket,
  setShareConfirmationShown, deleteTicket, clientName, selectedBuilding,
}: TicketSharePaneProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canDeleteTickets: boolean = isAdmin || permissions.includes(UserPermission.TICKETS_DELETE);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);

  const shareButton = () => (
    <PressableStyled
      hitSlop={{
        top: 10,
        left: 10,
        right: 10,
        bottom: 0,
      }}
      style={styles.dropdownButton}
      onPress={() => {
        logEvent('Tickets', 'share');
        setShareDropdownShown(false);
        const subDomain = isDev ? 'qainsights' : 'insights';
        Clipboard.setStringAsync(
          `https://${subDomain}.hereworks.tech/Tickets?clientId=${clientName}&reportId=${selectedTicket.id}&buildingId=${selectedBuilding}`,
        );
        setShareConfirmationShown(true);
        setDrawerOpen(false);
        const timeout = setTimeout(() => {
          setShareConfirmationShown(false);
          clearTimeout(timeout);
        }, 3000);
      }}
      testID='share-ticket-btn'
    >
      <Text style={styles.dropdownText}>Share</Text>
    </PressableStyled>
  );

  const deleteButton = () => (
    <Pressable
      hitSlop={{
        top: 0,
        left: 10,
        right: 10,
        bottom: 10,
      }}
      style={(state) => [
        styles.dropdownButton,
        state.hovered && Style.hovered, state.pressed && Style.pressed,
      ]}
      onPress={() => {
        logEvent('Tickets', 'delete');
        setShareDropdownShown(false);
        setAlertVisible(true);
      }}
      testID='delete-ticket-btn'
    >
      <Text style={styles.dropdownText}>Delete</Text>
    </Pressable>
  );

  return <View style={styles.container}>
    <Text style={styles.text}>
      {'TICKET ID: '}
      <Text
        style={{ color: Colors.light.hereworksDarkBlue }}
        selectable
      >
        {selectedTicket.id}
      </Text>
    </Text>
    <PressableStyled
      onPress={() => {
        logEvent('TicketSharePane', 'menu');
        if (isMobile) setDrawerOpen(!drawerOpen);
        else setShareDropdownShown(!shareDropdownShown);
      }}
      testID='ticket-menu-btn'
    >
      <EllipsisIcon fill={Colors.light.hereworksDarkBlue300} />
    </PressableStyled>
    <ShareTicketPopup visible={shareConfirmationShown} />
    {shareDropdownShown && (
      <View style={styles.dropdown}>
        {shareButton()}
        {canDeleteTickets && deleteButton()}
      </View>
    )}
    <SelectionModal
      isVisible={drawerOpen}
      hide={() => setDrawerOpen(false)}
      listContent={<>
        {shareButton()}
        {canDeleteTickets && deleteButton()}
        <AlertModal
          modalVisible={alertVisible}
          title={'Delete the Ticket'}
          message={<Text numberOfLines={2}>
            Do you want to delete the ticket -{' '}
            <Text numberOfLines={1} style={Style.alertBoldText}>
              {` "${selectedTicket.name}"`}
            </Text>
            ?
          </Text>}
          error={true}
          action={() => {
            deleteTicket();
            setDrawerOpen(false);
          }}
          actionTitle={'Delete'}
          closeTitle={'OK'}
          setModalVisible={setAlertVisible}
        />
      </>}
      title={selectedTicket.name}
    />
    {isMobile ? <></> : <AlertModal
      modalVisible={alertVisible}
      title={'Delete the Ticket'}
      message={<Text numberOfLines={2}>
        Do you want to delete the ticket -{' '}
        <Text numberOfLines={1} style={Style.alertBoldText}>
          {` "${selectedTicket.name}"`}
        </Text>
        ?
      </Text>}
      error={true}
      action={() => {
        deleteTicket();
        setDrawerOpen(false);
      }}
      actionTitle={'Delete'}
      closeTitle={'OK'}
      setModalVisible={setAlertVisible}
    />}
  </View>;
};

const styles = StyleSheet.create({
  container: {
    minHeight: 40,
    paddingVertical: 8,
    paddingHorizontal: isMobile ? 16 : 24,
    borderTopStartRadius: 12,
    borderTopEndRadius: 12,
    backgroundColor: Colors.light.hereworksWhite200,
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
    zIndex: 2,
  },
  text: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack400,
    paddingEnd: 16,
    flex: 1,
  },
  dropdown: {
    position: 'absolute',
    backgroundColor: '#FFF',
    borderColor: Colors.light.hereworksBlue100,
    borderWidth: 1.5,
    end: 20,
    top: 45,
    borderRadius: 12,
    zIndex: 1,
  },
  dropdownButton: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    width: '100%',
    alignItems: 'flex-start',
  },
  dropdownText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksPrimaryGrey,
    textAlign: 'center',
  },
});

export default TicketSharePane;
